import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Modal from '../ui/Modal';
import Breadcrumb from '../ui/Breadcrumb';
import { IconAlertTriangle, IconCheck, IconFlag, IconGlobe, IconPlus } from '../ui/icons';

const COMMON_LANGUAGES = [
  { lang_code: 'fr', label: 'Français' },
  { lang_code: 'en', label: 'English' },
  { lang_code: 'es', label: 'Español' },
  { lang_code: 'de', label: 'Deutsch' },
  { lang_code: 'it', label: 'Italiano' },
  { lang_code: 'nl', label: 'Nederlands' },
  { lang_code: 'pt', label: 'Português' },
  { lang_code: 'pt-BR', label: 'Português (Brasil)' },
];

function normalizeCode(input) {
  const raw = String(input || '').trim().replaceAll('_', '-');
  if (!raw) return '';
  const endsWithDash = raw.endsWith('-');
  const parts = raw.split('-').filter((p) => String(p || '').trim() !== '');
  const first = parts[0]?.toLowerCase() || '';
  const rest = parts.slice(1).map((p) => (p.length === 2 ? p.toUpperCase() : p));
  const joined = [first, ...rest].filter(Boolean).join('-');
  if (endsWithDash && joined) return `${joined}-`;
  return joined;
}

function fingerprintLanguagesDraft(draft) {
  const list = Array.isArray(draft?.languages) ? draft.languages : [];
  const normalized = list
    .map((l) => ({
      lang_code: String(l.lang_code || ''),
      label: String(l.label || ''),
      is_enabled: Boolean(l.is_enabled),
      is_default: Boolean(l.is_default),
      sort_order: Number.isFinite(l.sort_order) ? Number(l.sort_order) : 0,
    }))
    .sort((a, b) => a.lang_code.localeCompare(b.lang_code, 'en'));
  return JSON.stringify({ default_lang: String(draft?.default_lang || ''), languages: normalized });
}

export default function LanguagesPage({ siteId, site, onUnsavedChange }) {
  const [state, setState] = useState({ status: 'loading', error: '', saving: false });
  const [draft, setDraft] = useState({ languages: [], default_lang: 'fr' });
  const [modal, setModal] = useState(null);
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const savedDraftRef = useRef(null);
  const saveLatestRef = useRef(null);
  const discardLatestRef = useRef(null);
  const saveFromBar = useCallback(() => saveLatestRef.current?.(), []);
  const discardFromBar = useCallback(() => discardLatestRef.current?.(), []);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setState({ status: 'loading', error: '', saving: false });
    api
      .siteLanguages(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', error: res.error || 'Impossible de charger', saving: false });
          return;
        }
        const next = { languages: res.languages || [], default_lang: res.default_lang || 'fr' };
        setDraft(next);
        const fp = fingerprintLanguagesDraft(next);
        setSavedFingerprint(fp);
        savedDraftRef.current = next;
        setState({ status: 'ready', error: '', saving: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', error: 'Impossible de charger', saving: false });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const enabled = useMemo(() => (draft.languages || []).filter((l) => l.is_enabled), [draft.languages]);

  const hasDefault = useMemo(() => enabled.some((l) => l.lang_code === draft.default_lang), [enabled, draft.default_lang]);

  const isDirty = useMemo(() => Boolean(savedFingerprint && fingerprintLanguagesDraft(draft) !== savedFingerprint), [draft, savedFingerprint]);

  useEffect(() => {
    const label = 'Langues';
    onUnsavedChange?.({ dirty: isDirty, label, onSave: saveFromBar, onDiscard: discardFromBar });
    return () => onUnsavedChange?.({ dirty: false, label: '', onSave: null, onDiscard: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, saveFromBar, discardFromBar]);

  function addLanguage(lang) {
    setDraft((d) => {
      const exists = (d.languages || []).some((x) => x.lang_code === lang.lang_code);
      if (exists) {
        return {
          ...d,
          languages: (d.languages || []).map((x) => (x.lang_code === lang.lang_code ? { ...x, ...lang, is_enabled: true } : x)),
        };
      }
      const next = [...(d.languages || []), { ...lang, is_enabled: true, is_default: false, sort_order: (d.languages || []).length * 10 }];
      return { ...d, languages: next };
    });
  }

  async function save() {
    if (!siteId) return;
    const list = (draft.languages || []).map((l, idx) => ({ ...l, sort_order: Number.isFinite(l.sort_order) ? l.sort_order : idx * 10 }));
    const enabledList = list.filter((l) => l.is_enabled);
    const defaultLang = enabledList.some((l) => l.lang_code === draft.default_lang) ? draft.default_lang : enabledList[0]?.lang_code || 'fr';
    const payload = {
      site_id: Number(siteId),
      languages: list.map((l) => ({ ...l, is_default: l.lang_code === defaultLang })),
    };
    setState((s) => ({ ...s, saving: true, error: '' }));
    const res = await api.siteLanguagesUpdate(payload);
    if (!res.ok) {
      setState((s) => ({ ...s, saving: false, error: res.error || 'Enregistrement impossible' }));
      return;
    }
    // Recharge propre
    const fresh = await api.siteLanguages(Number(siteId));
    if (fresh.ok) {
      const next = { languages: fresh.languages || [], default_lang: fresh.default_lang || defaultLang };
      setDraft(next);
      const fp = fingerprintLanguagesDraft(next);
      setSavedFingerprint(fp);
      savedDraftRef.current = next;
    }
    setState((s) => ({ ...s, saving: false }));
  }

  saveLatestRef.current = save;
  discardLatestRef.current = () => {
    if (!savedDraftRef.current) return;
    const next = savedDraftRef.current;
    setDraft(next);
    setSavedFingerprint(fingerprintLanguagesDraft(next));
  };

  if (!siteId) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Langues</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Langues' }]} />
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card">
        <div className="Row" style={{ marginBottom: 10 }}>
          <div>
            <div className="TileTitle">Langues du site</div>
          </div>
          <div className="Badges">
            <button type="button" className="Button Button--ghost" onClick={() => setModal({ type: 'add-lang', q: '' })}>
              <span className="ButtonInline">
                <IconPlus /> Ajouter
              </span>
            </button>
          </div>
        </div>

        {!hasDefault ? (
          <div className="Notice" style={{ marginBottom: 12 }}>
            <span className="ButtonInline">
              <IconAlertTriangle /> Aucune langue par défaut valide: elle sera automatiquement choisie lors de l’enregistrement.
            </span>
          </div>
        ) : null}

        <div className="List" role="list">
          {(draft.languages || []).map((l) => {
            const enabled = Boolean(l.is_enabled);
            const isDefault = enabled && l.lang_code === draft.default_lang;
            return (
              <div key={l.lang_code} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="Badge">
                      <span className="ButtonInline">
                        <IconFlag /> {l.lang_code}
                      </span>
                    </span>
                    <span style={{ minWidth: 0 }}>{l.label}</span>
                    {isDefault ? (
                      <span className="Badge" style={{ borderColor: 'var(--good)', color: 'var(--good)' }}>
                        <span className="ButtonInline">
                          <IconCheck /> Défaut
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="Badges">
                  <button
                    type="button"
                    className={`TogglePill ${enabled ? 'is-on' : ''}`}
                    onClick={() => {
                      const next = !enabled;
                      setDraft((d) => {
                        const nextLanguages = (d.languages || []).map((x) =>
                          x.lang_code === l.lang_code ? { ...x, is_enabled: next, is_default: false } : x
                        );
                        const wasDefault = d.default_lang === l.lang_code;
                        if (wasDefault && !next) {
                          const fallback = nextLanguages.find((x) => x.lang_code !== l.lang_code && x.is_enabled)?.lang_code || 'fr';
                          return { ...d, languages: nextLanguages, default_lang: fallback };
                        }
                        return { ...d, languages: nextLanguages };
                      });
                    }}
                    aria-pressed={enabled ? 'true' : 'false'}
                  >
                    {enabled ? <IconCheck /> : <IconFlag />}
                    <span>Activée</span>
                  </button>

                  <button
                    type="button"
                    className={`TogglePill ${isDefault ? 'is-on' : ''}`}
                    disabled={!enabled}
                    onClick={() => setDraft((d) => ({ ...d, default_lang: l.lang_code }))}
                    aria-pressed={isDefault ? 'true' : 'false'}
                    title={enabled ? 'Définir comme langue par défaut' : 'Active d’abord cette langue'}
                  >
                    {isDefault ? <IconCheck /> : <IconFlag />}
                    <span>Défaut</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal?.type === 'add-lang' ? (
        <AddLanguageModal
          onClose={() => setModal(null)}
          onPick={(l) => {
            addLanguage(l);
            setModal(null);
          }}
        />
      ) : null}
    </div>
  );
}

function AddLanguageModal({ onClose, onPick }) {
  const [q, setQ] = useState('');
  const [custom, setCustom] = useState({ lang_code: '', label: '' });
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return COMMON_LANGUAGES;
    return COMMON_LANGUAGES.filter((l) => l.lang_code.toLowerCase().includes(query) || l.label.toLowerCase().includes(query));
  }, [q]);

  return (
    <Modal
      title="Ajouter une langue"
      icon={<IconPlus />}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="Button Button--ghost" onClick={onClose}>
            Fermer
          </button>
        </>
      }
    >
      <div className="Field">
        <label className="Label">Rechercher</label>
        <input className="Input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="fr, en, español…" />
      </div>

      <div className="TemplateGrid" role="list" style={{ marginTop: 10 }}>
        {filtered.map((l) => (
          <button key={l.lang_code} type="button" className="TemplateButton" onClick={() => onPick({ ...l })} style={{ '--accent': '#0ea5e9' }}>
            <div className="TemplateButtonIcon" aria-hidden="true">
              <IconGlobe />
            </div>
            <div className="TemplateButtonText">
              <div className="TemplateButtonTitle">{l.label}</div>
              <div className="TemplateButtonHint">{l.lang_code}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="RoleBox" style={{ marginTop: 12 }}>
        <div className="TileTitle" style={{ marginBottom: 8 }}>
          Langue personnalisée
        </div>
        <div className="FormGrid">
          <div className="Field">
            <label className="Label">Code (ex: fr, en, en-US)</label>
            <input
              className="Input"
              value={custom.lang_code}
              onChange={(e) => setCustom((c) => ({ ...c, lang_code: normalizeCode(e.target.value) }))}
              placeholder="en-US"
            />
          </div>
          <div className="Field">
            <label className="Label">Label</label>
            <input className="Input" value={custom.label} onChange={(e) => setCustom((c) => ({ ...c, label: e.target.value }))} placeholder="English (US)" />
          </div>
        </div>
        <div className="Badges" style={{ justifyContent: 'flex-start' }}>
          <button
            type="button"
            className="Button"
            disabled={!custom.lang_code.trim()}
            onClick={() => {
              const code = normalizeCode(custom.lang_code).replace(/-+$/, '');
              onPick({ lang_code: code, label: custom.label.trim() || code });
            }}
          >
            <span className="ButtonInline">
              <IconPlus /> Ajouter
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
